import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import type { SingleValue } from 'react-select';
import cloneDeep from 'lodash/cloneDeep';
import parse from 'html-react-parser';
import CustomFields from './customDetails/CustomFields';
import './EditMultipleCards.css';
import CustomFieldsService from '@/api/CustomFieldsService';
import conversationApi from '@/services/conversationApi';
import { Modal } from '@/components/utils/Modal';
import { useToast } from '@components/ui/Toast';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SelectOption {
  value: string;
  label: string;
}

interface ConversationLayer {
  LayerId: number;
}

interface CustomField {
  CustomFieldId: number;
  Value: string;
  CoreTaskId: number;
  FieldTypeId: number;
  AssociatedImage?: string;
  CustomFieldValueId?: number;
  WasEdited?: number;
}

interface CustomFieldLayer {
  CustomFields: CustomField[];
}

interface ConversationPhase {
  Name: string;
  ConversationPhaseId: string;
  CoreTaskId: string;
  ConversationId: number;
}

/** Row returned by UpdateMultipleConversations, one per affected conversation */
interface PreviewRow {
  ConversationId: number;
  Title: string;
  Body: string;
  /** 1 = will be updated, 0 = backend message */
  ResultType: number;
}

interface SkippedCard {
  id: string;
  title: string;
}

interface PreviewSummary {
  message: string;
  updatedCount: number;
  totalCount: number;
  skipped: SkippedCard[];
  /** False for layer/owner-only edits: the preview does not report those */
  countsAreReliable: boolean;
}

interface ConversationOwner {
  FieldOwner: string;
  Edit: number;
}

interface MultiEditionData {
  multiEditionLayers: unknown;
  multiEditionOwners: ConversationOwner[];
  multiEditionCustomFields: CustomFieldLayer[];
  multiEditionPhases: ConversationPhase[];
}

interface Conversation {
  layers?: unknown;
  OwnedBy?: string;
  OwnedById?: string;
  OwnersConversations?: string;
  ConversationLayers: ConversationLayer[];
  ProjectId?: string;
  ConfigValueId?: string;
}

interface HierarchyItem {
  ConversationId: number;
}

interface ConfigFunctions {
  getSocketConnectionId?: () => string;
  onclose?: () => void;
  reloadConversations?: (data: unknown, backup: unknown) => void;
}

export interface EditMultipleCardsConfig {
  api: any;
  conversation: Conversation;
  ConversationIds: string | string[];
  /** Id + title of the selected cards, used to name the ones left out */
  SelectedConversations?: Array<{ ConversationId: number; Title: string }>;
  DashboardId: string;
  title?: string;
  width?: number;
  height?: number;
  isdraggable?: boolean;
  closeonescape?: boolean;
  allowMinimize?: boolean;
  allowMaximize?: boolean;
  showHeader?: boolean;
  backupConversations?: unknown;
  UpdateCustomFields?: unknown;
  functions: ConfigFunctions;
}

interface EditMultipleCardsProps {
  config: EditMultipleCardsConfig;
  isOpen?: boolean;
  onIsOpen: () => void;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const EditMultipleCards: React.FC<EditMultipleCardsProps> = ({ config, isOpen, onIsOpen }) =>
{
  const [showCustomFields, setShowCustomFields] = useState(false);
  const [conversation, setConversation] = useState<Conversation>([] as unknown as Conversation);
  const [multiEdition, setMultiEdition] = useState<MultiEditionData | null>(null);
  const [layersCustomFieldsOriginal, setLayersCustomFieldsOriginal] = useState<CustomFieldLayer[]>(
    [],
  );
  const [layersCustomFieldsChanges, setLayersCustomFieldsChanges] = useState<CustomFieldLayer[]>(
    [],
  );
  const [layerIdsOriginal, setLayerIdsOriginal] = useState<number[]>([]);
  const [layerIdsChanges, setLayerIdsChanges] = useState<number[] | ''>('');
  const [_ownerOriginal, setOwnerOriginal] = useState('');
  const [ownerChanges, setOwnerChanges] = useState('');
  const [viewType, setViewType] = useState<1 | 2>(1);
  const [dataPreview, setDataPreview] = useState<PreviewRow[]>([]);
  const [previewSummary, setPreviewSummary] = useState<PreviewSummary | null>(null);
  const [showSkippedList, setShowSkippedList] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [layersInitial, setLayersInitial] = useState<ConversationLayer[]>([]);
  const [canEditOwner, setCanEditOwner] = useState(true);
  const [conversationsPhases, setConversationsPhases] = useState<ConversationPhase[]>([]);
  const [selectedPhase, setSelectedPhase] = useState('All phases');
  const [selectedPhaseLabel, setSelectedPhaseLabel] = useState('All phases');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [conversationIds, setConversationIds] = useState<string>('');

  const toast = useToast();

  // ── Helpers ────────────────────────────────────────────────────────────────

  const splitIds = (ids: string | string[]): string[] =>
    String(ids)
      .split(',')
      .map(id => id.trim())
      .filter(Boolean);

  // The backend casts every id to int, so non-numeric tokens break the stored procedure
  const sanitizeIds = (ids: string[]): string[] => ids.filter(id => /^\d+$/.test(id));

  const mergeConversationIds = (
    objConversations: HierarchyItem[],
    strConversations: string | string[],
  ): string =>
  {
    const fromObj = objConversations.map(item => String(item.ConversationId).trim());
    const fromStr = splitIds(strConversations);
    return sanitizeIds([...new Set([...fromObj, ...fromStr])]).join(',');
  };

  // Phase requests carry ConversationPhaseIds but the backend answers with the parent id
  const getTargetConversations = (): SkippedCard[] =>
  {
    const titleById = new Map<string, string>(
      (config.SelectedConversations ?? []).map(c => [String(c.ConversationId), c.Title]),
    );

    const phaseIds =
      selectedPhaseLabel !== 'All phases'
        ? [
          ...new Set(
            conversationsPhases
              .filter(p => p.Name === selectedPhaseLabel && p.ConversationId)
              .map(p => String(p.ConversationId)),
          ),
        ]
        : [];

    const ids = phaseIds.length > 0 ? phaseIds : splitIds(conversationIds);

    return ids.map(id => ({ id, title: titleById.get(id) ?? `#${id}` }));
  };

  const buildPreviewSummary = (rows: PreviewRow[], detailsChanged: boolean): PreviewSummary =>
  {
    const updatedIds = new Set(
      rows.filter(r => Number(r.ResultType) !== 0).map(r => String(r.ConversationId)),
    );
    const targets = getTargetConversations();
    const skipped = targets.filter(t => !updatedIds.has(t.id));
    const messageRow = rows.find(r => Number(r.ResultType) === 0);

    return {
      message: messageRow?.Title ?? '',
      updatedCount: targets.length - skipped.length,
      totalCount: targets.length,
      skipped,
      countsAreReliable: detailsChanged,
    };
  };

  const getMergedCustomFieldsByIds = (
    coreTaskIds: string,
    newSelectedPhase?: string,
    ids?: string,
  ) =>
  {
    CustomFieldsService.GetMergedCustomFieldsByIds({
      api: config.api,
      conversationIds: ids ?? conversationIds,
      coreTaskIds: coreTaskIds,
    }).then((data: MultiEditionData) =>
    {
      if (data)
      {
        const owner = data.multiEditionOwners?.[0];
        const conversationLayers = config.conversation.ConversationLayers ?? [];

        config.conversation.layers = data.multiEditionLayers;
        config.conversation.OwnedBy = owner?.FieldOwner ?? '';

        setMultiEdition(data);
        setConversation(config.conversation);
        setShowCustomFields(true);
        setLayersCustomFieldsOriginal(cloneDeep(data.multiEditionCustomFields));
        setLayerIdsOriginal(conversationLayers.map(l => l.LayerId));
        setOwnerOriginal(owner?.FieldOwner ?? '');
        setLayersInitial(cloneDeep(conversationLayers));
        setCanEditOwner(owner?.Edit === 1);
        setConversationsPhases(data.multiEditionPhases ?? []);
      }

      const phase = newSelectedPhase ?? 'All phases';
      setSelectedPhase(phase);
      setSelectedPhaseLabel(phase);
      config.showHeader = phase === 'All phases';
      setIsLoading(false);
    }).catch(err =>
    {
      console.error('[MultiEdit] GetMergedCustomFieldsByIds error', err);
      setIsLoading(false);
      setShowAlert(true);
      setAlertMessage(getRequestErrorMessage(err));
    });
  };

  const getCustomFields = (coreTaskIds: string, newSelectedPhase?: string) =>
  {
    setIsLoading(true);
    conversationApi
      .GetConversationsHierarchy(config.api.url, config.api.token, {
        conversationIds: config.ConversationIds,
        dashboardId: config.DashboardId,
      })
      .then((data: HierarchyItem[]) =>
      {
        // The action menus pass an array, so always normalise to a comma-separated string
        const merged =
          data?.length > 0
            ? mergeConversationIds(data, config.ConversationIds)
            : sanitizeIds(splitIds(config.ConversationIds)).join(',');

        setConversationIds(merged);
        getMergedCustomFieldsByIds(coreTaskIds, newSelectedPhase, merged);
      })
      .catch(err =>
      {
        // The hierarchy only adds child cards, so continue with the selected ones
        console.error('[MultiEdit] GetConversationsHierarchy error', err);
        const selected = sanitizeIds(splitIds(config.ConversationIds)).join(',');
        setConversationIds(selected);
        getMergedCustomFieldsByIds(coreTaskIds, newSelectedPhase, selected);
      });
  };

  useEffect(() =>
  {
    getCustomFields('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Save / Back ────────────────────────────────────────────────────────────

  /** Message returned by the Web API on a 4xx/5xx */
  const getRequestErrorMessage = (err: any): string =>
  {
    const data = err?.response?.data;
    const backendMessage =
      typeof data === 'string'
        ? data
        : (data?.Message ?? data?.message ?? data?.ExceptionMessage ?? data?.MessageDetail);

    const status = err?.response?.status;
    const prefix = status ? `The update could not be processed (${status}).` : 'The update could not be processed.';

    return backendMessage ? `${prefix} ${backendMessage}` : `${prefix} ${err?.message ?? ''}`.trim();
  };

  const saveOption = (option: 0 | 1) =>
  {
    // Ignore clicks while the request is in flight
    if (isSaving) return;

    // Flatten original custom fields for comparison
    const originalsFlat = layersCustomFieldsOriginal.filter(Boolean).flatMap(layer =>
      layer.CustomFields.map(cf => ({
        CustomFieldId: cf.CustomFieldId,
        Value: cf.Value,
        CoreTaskID: cf.CoreTaskId,
        FieldTypeId: cf.FieldTypeId,
        AssociatedImage: cf.AssociatedImage,
        CustomFieldValueId: cf.CustomFieldValueId,
      })),
    );

    // Only keep fields that were actually changed
    const changedFields: CustomField[] = layersCustomFieldsChanges.flatMap(layer =>
      layer.CustomFields.filter(cf =>
      {
        const original = originalsFlat.find(o => o.CustomFieldId === cf.CustomFieldId);
        return original && original.Value !== cf.Value;
      }).map(cf => ({ ...cf, WasEdited: 1 })),
    );

    const isEqualLayers = (a: number[], b: number[]) =>
      JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

    const layersUnchanged =
      layerIdsChanges === '' || isEqualLayers(layerIdsChanges as number[], layerIdsOriginal);

    const ownerUnchanged = ownerChanges.toLowerCase().trim() === '';

    if (changedFields.length === 0 && layersUnchanged && ownerUnchanged)
    {
      setShowAlert(true);
      setAlertMessage('Please change at least one custom field and try again.');
      return;
    }

    const phasesToUpdate = sanitizeIds(
      conversationsPhases
        .filter(p => p.Name === selectedPhaseLabel)
        .map(p => String(p.ConversationPhaseId).trim()),
    ).join(',');

    // Backend requires the full layer custom fields snapshot even when only layers changed
    const fullLayerCustomFields =
      layersCustomFieldsChanges.length > 0 ? layersCustomFieldsChanges : layersCustomFieldsOriginal;

    const request = {
      Conversation: { ...conversation, LayerCustomFields: fullLayerCustomFields },
      ConversationMultiEditionIds:
        phasesToUpdate === '' ? conversationIds.toString() : phasesToUpdate,
      CustomFields: changedFields,
      IsPreview: option,
      LayerCustomFields: fullLayerCustomFields,
      LayersModified: layersUnchanged ? '' : (layerIdsChanges as number[]).join(','),
      OwnerConversations: !ownerUnchanged ? conversationIds.toString() : '',
      OwnerModified: !ownerUnchanged ? (conversation.OwnedById ?? '') : '',
      SocketConnectionId: config.functions?.getSocketConnectionId?.() ?? '',
    };

    setIsSaving(true);

    conversationApi
      .Updatemultipleconversations(config.api.url, config.api.token, request)
      .then(
        (
          data: { layerCustomFields?: CustomFieldLayer[]; Body?: string } & Array<{ Body: string }>,
        ) =>
        {
          setIsSaving(false);

          if (option === 1)
          {
            if (!data) return;
            const rows = (Array.isArray(data) ? data : []) as unknown as PreviewRow[];
            setViewType(2);
            setDataPreview(rows);
            setShowSkippedList(false);
            setPreviewSummary(buildPreviewSummary(rows, changedFields.length > 0));
          }
          else
          {
            if (config.functions.reloadConversations)
            {
              config.functions.reloadConversations(data, conversation);
            }
            const updatedCount = Array.isArray(data) ? data.length : 0;
            toast.success(
              `${updatedCount} item${updatedCount !== 1 ? 's' : ''} updated`,
            );
            onClose();
          }
        },
      )
      .catch(err =>
      {
        setIsSaving(false);
        console.error('[MultiEdit] saveOption error', err);

        setViewType(1);
        setShowAlert(true);
        setAlertMessage(getRequestErrorMessage(err));
      });
  };

  const backOption = () =>
  {
    setViewType(1);
    setDataPreview([]);
    setPreviewSummary(null);
    setShowSkippedList(false);
  };

  // ── Event handlers ─────────────────────────────────────────────────────────

  const onClose = () =>
  {
    onIsOpen();
  };

  const onChangedLayers = (params: Conversation) =>
  {
    let layers = params.ConversationLayers.map(l => l.LayerId);
    if (layers.length === 0)
    {
      layers = layersInitial.map(l => l.LayerId);
      params.ConversationLayers = layersInitial;
    }
    setConversation(params);
    setLayerIdsChanges(layers);
    setShowAlert(false);
  };

  const onChangedOwner = (params: Conversation) =>
  {
    setConversation(params);
    setOwnerChanges(params.OwnedBy?.toString() ?? '');
    setShowAlert(false);
  };

  const updateDetailsConversationReact = (fields: CustomFieldLayer[]) =>
  {
    setLayersCustomFieldsChanges(fields);
    setShowAlert(false);
  };

  const onPhaseSelect = (option: SingleValue<SelectOption>) =>
  {
    const label = option?.value ?? 'All phases';
    setSelectedPhaseLabel(label);
    setIsLoading(true);
    const coreTaskIds = option?.value
      ? conversationsPhases
        .filter(p => p.Name === option.value)
        .map(p => p.CoreTaskId)
        .join(',')
      : '';
    getMergedCustomFieldsByIds(coreTaskIds, label);
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const phasesList: SelectOption[] = [
    { value: 'All phases', label: 'All phases main page' },
    ...conversationsPhases
      .filter((phase, index, self) => index === self.findIndex(t => t.Name === phase.Name))
      .map(p => ({ value: p.Name, label: p.Name })),
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!isOpen) return null;
  const comboBoxSelectStyles: Record<string, (base: any, state?: any) => any> = {
    control: (base: any) => ({
      ...base,
      minHeight: 30,
      fontSize: 11,
      borderColor: '#ccc',
      boxShadow: 'none',
      width: '180px',
      '&:hover': { borderColor: '#aaa' },
    }),
    menu: (base: any) => ({
      ...base,
      marginTop: 0,
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      fontSize: '10px',
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: '#fff',
      borderColor: '#ccc',
      borderWidth: '1px',
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      fontSize: '4px',
      color: '#ccc',
      ':hover': {
        backgroundColor: '#fff',
      },
    }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: 12,
      padding: '5px 12px',
      backgroundColor: state.isSelected ? '#62accb77' : state.isFocused ? '#62accb' : undefined,
      color: state.isSelected || state.isFocused ? '#fff' : '#333',
      cursor: 'pointer',
    }),
  };
  return (
    <div className="modalEditMultipleCards">
      <div className="multipleCardsContainerParent">
        <Modal
          title={config.title}
          open={isOpen}
          onClose={onIsOpen}
          preventCloseClickOut
          className="!w-full !max-w-[500px] !h-[507px] multiedit-modal-panel"
        >
          <div className="multiedit-modal-content">
            {/* Phase selector */}
            {viewType === 1 && (
              <div className="input-container">
                <div className="input-group ig-full-width">
                  <div className="phase-selector-container">
                    <span className="phase-selector-label">Select a phase:</span>
                    <Select
                      autoFocus={false}
                      options={phasesList}
                      onChange={onPhaseSelect}
                      value={phasesList.find(o => o.value === selectedPhaseLabel) ?? null}
                      isMulti={false}
                      isSearchable={false}
                      closeMenuOnSelect
                      isClearable={!!selectedPhaseLabel && selectedPhaseLabel !== 'All phases'}
                      styles={comboBoxSelectStyles}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Edit view */}
            {viewType === 1 && !isLoading && (
              <div className="multiedit-view">
                {showCustomFields && (
                  <div className="corgee-multiple-edit-cards-customfields layer-custom-fields-container">
                    {React.createElement(CustomFields as any, {
                      key: selectedPhase,
                      section: 'section',
                      config: config,
                      item: conversation,
                      projectId: conversation.ProjectId,
                      configValueId: conversation.ConfigValueId,
                      groups: [],
                      isConversationDetailsView: true,
                      isProjectView: true,
                      UpdateCustomFields: config.UpdateCustomFields,
                      submitTime: false,
                      onOpenEditHistory: () => {},
                      updateCustomFieldsHistory: () => {},
                      editProjectDetails: true,
                      multiEdition: multiEdition,
                      onChangedLayers: onChangedLayers,
                      updateDetailsConversationReact: updateDetailsConversationReact,
                      onChangedOwner: onChangedOwner,
                      canEditOwner: canEditOwner,
                    })}
                  </div>
                )}

                {showAlert && (
                  <div className="multi-edition-validation-message">
                    <div>{alertMessage}</div>
                  </div>
                )}

                <div className="react-corgee-wizard_modal-footer flex gap-x-2 justify-end">
                  <input
                    className="react-corgee-wizard_cancel-btn"
                    type="button"
                    value="Cancel"
                    onClick={onIsOpen}
                  />
                  <button
                    className="react-corgee-wizard_corgee-btn"
                    type="button"
                    onClick={() => saveOption(1)}
                    disabled={isSaving}
                  >
                    Preview
                    {isSaving && (
                      <span className="multieditor-btn-spinner" role="status" aria-label="Loading" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Loading spinner */}
            {viewType === 1 && isLoading && (
              <div className="spinner-container-loading">
                <div className="spinner-border-multieditor" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}

            {/* Preview view */}
            {viewType === 2 && (
              <div className="multiedit-view">
                {previewSummary && (
                  <div className="multiedit-preview-summary">
                    {previewSummary.message !== '' && (
                      <div className="multiedit-preview-message">{previewSummary.message}</div>
                    )}

                    {!previewSummary.countsAreReliable && (
                      <div className="multiedit-preview-note">
                        Layer and owner changes are applied to every selected card and are not
                        listed in this preview.
                      </div>
                    )}

                    {previewSummary.countsAreReliable && (
                      <>
                        <div className="multiedit-preview-counts">
                          <span className="multiedit-preview-count multiedit-preview-count--ok">
                            {previewSummary.updatedCount} of {previewSummary.totalCount} card
                            {previewSummary.totalCount !== 1 ? 's' : ''} will be updated
                          </span>
                          {previewSummary.skipped.length > 0 && (
                            <button
                              type="button"
                              className="multiedit-preview-count multiedit-preview-count--skipped"
                              onClick={() => setShowSkippedList(v => !v)}
                              aria-expanded={showSkippedList}
                            >
                              {previewSummary.skipped.length} will not be updated
                              <span className="multiedit-preview-caret">
                                {showSkippedList ? '▲' : '▼'}
                              </span>
                            </button>
                          )}
                        </div>

                        {showSkippedList && previewSummary.skipped.length > 0 && (
                          <div className="multiedit-preview-skipped">
                            <div className="multiedit-preview-skipped-reason">
                              These cards do not have the layer or the detail being edited:
                            </div>
                            <ul className="multiedit-preview-skipped-list">
                              {previewSummary.skipped.map(card => (
                                <li key={card.id} title={card.title}>
                                  {card.title}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="corgee-multiple-edit-cards-customfields">
                  {parse(dataPreview[0]?.Body ?? '')}
                </div>

                {showAlert && (
                  <div className="multi-edition-validation-message">
                    <div>{alertMessage}</div>
                  </div>
                )}

                <div className="react-corgee-wizard_modal-footer flex gap-x-2 justify-end">
                  <input
                    className="react-corgee-wizard_cancel-btn"
                    type="button"
                    value="Back"
                    onClick={backOption}
                  />

                  <button
                    className="react-corgee-wizard_corgee-btn"
                    type="button"
                    onClick={() => saveOption(0)}
                    disabled={isSaving}
                  >
                    Apply
                    {isSaving && (
                      <span className="multieditor-btn-spinner" role="status" aria-label="Saving" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default EditMultipleCards;
